import { getUserPostComments, getUserCommentReplies, createComment, getComment } from '../../api/api';
import { CommentActionBar } from './ActionBar';
import { useWebSocket } from '../../hooks/useWebSocket';
import { getWebSocketUrl } from '../../config';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import UserLink from '../UserLink';
import { defaultPfpUrl, formatRelativeTimestamp } from "../utils";
import { FormattedBody } from '../ui/formatted-body';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { DisclosureChevronIcon } from '../../widgets/icons';
import ContentOverflowMenu from './ContentOverflowMenu';
import { useAdminModeration } from '../../context/AdminModerationContext';
import {
    buildOptimisticComment,
    isOptimisticCommentId,
    mergeFetchedComments,
    mergeIncomingComment,
    removeCommentById,
} from './commentOptimistic';

const FOCUS_PAGE_SIZE = 50;
const FOCUS_FLASH_MS = 1600;

/**
 * Deepest level that may be replied to. Depth 0 is a top-level comment, so
 * replies can be created at depths 1 through MAX_REPLY_DEPTH. This only gates
 * the Reply button; stored threads and rendering support any depth.
 */
const MAX_REPLY_DEPTH = 2;

/** Direct children needed before an expanded thread can be collapsed. */
const MIN_REPLIES_TO_COLLAPSE = 3;

/** Depth at which reply indentation stops growing (see .thread-replies--tight). */
const INDENT_CAP_DEPTH = 4;

/** Avatar sizes drive the connector geometry in index.css — keep them in sync. */
const ROOT_AVATAR_CLASS = "w-10 h-10";
const REPLY_AVATAR_CLASS = "w-8 h-8";

/** Branch rows without an avatar end their connector on the text instead. */
const STUB_BRANCH_STYLE = { "--thread-elbow-h": "1rem" };

/**
 * Walk parent_id links from a comment up to the top-level comment.
 * @param {number} commentId
 * @returns {Promise<Array<object>>}
 */
async function resolveCommentFocusChain(commentId) {
    const chain = [];
    const seen = new Set();
    let currentId = Number(commentId);
    while (Number.isFinite(currentId) && currentId > 0 && !seen.has(currentId)) {
        seen.add(currentId);
        const result = await getComment(currentId);
        if (!result?.success || !result.data) {
            break;
        }
        chain.unshift(result.data);
        currentId = Number(result.data.parent_id) || 0;
    }
    return chain;
}

/**
 * Recursive comment or reply. Renders its own list item so that a deleted
 * comment leaves no empty branch behind for the connector to hang from.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {object} props.comment
 * @param {"post"|"event"} [props.contentType]
 * @param {object} [props.content]
 * @param {number} [props.depth] - 0 for a top-level comment.
 * @param {number|null} [props.focusCommentId]
 * @param {(commentId: number) => void} [props.onDeleted]
 */
const Comment = ({
    userId,
    user,
    comment,
    contentType,
    content,
    depth = 0,
    focusCommentId = null,
    focusChainIds = [],
    focusedCommentsById = {},
    onDeleted,
}) => {
    const moderation = useAdminModeration();
    const rootRef = useRef(null);
    const repliesRef = useRef([]);
    const [showReplies, setShowReplies] = useState(false);
    const [replies, setReplies] = useState([]);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [replyCount, setReplyCount] = useState(comment.reply_count || 0);
    const [flashing, setFlashing] = useState(false);
    const [removed, setRemoved] = useState(false);

    repliesRef.current = replies;

    const isFocusTarget = focusCommentId != null && Number(focusCommentId) === Number(comment.id);
    const chainIndex = focusChainIds.indexOf(Number(comment.id));
    const nextFocusId = chainIndex >= 0 ? focusChainIds[chainIndex + 1] : null;

    const applyIncomingReply = useCallback((reply, { fromSelf = false, replaceTempId } = {}) => {
        if (!reply || reply.id == null) {
            return;
        }
        let added = false;
        setReplies((prev) => {
            const result = mergeIncomingComment(prev, reply, { replaceTempId });
            added = result.added;
            return result.list;
        });
        if (added) {
            setReplyCount((count) => count + 1);
        }
        if (fromSelf || replaceTempId) {
            setShowReplies(true);
        }
    }, []);

    const handleIncomingMessage = useCallback((data) => {
        if (data.type === 'new_reply') {
            applyIncomingReply(data.reply, {
                fromSelf: Number(data.reply?.author_id) === Number(userId),
            });
        }
    }, [applyIncomingReply, userId]);

    const isOptimistic = isOptimisticCommentId(comment.id);
    useWebSocket(
        isOptimistic ? null : getWebSocketUrl(`/ws/replies/${comment.id}/`),
        handleIncomingMessage
    );

    const fetchReplies = async () => {
        if (isOptimisticCommentId(comment.id)) {
            return;
        }
        if (replyCount === repliesRef.current.length && repliesRef.current.length > 0) {
            return;
        }
        if (repliesRef.current.length === 0) {
            setLoadingReplies(true);
        }
        try {
            const fetchedReplies = await getUserCommentReplies(
                userId,
                comment.id,
                repliesRef.current.length,
                8
            );
            const page = fetchedReplies.data || [];
            setReplies((prev) => mergeFetchedComments(prev, page));
        } catch (e) {
            console.error("Error fetching replies:", e);
        }
        setLoadingReplies(false);
    };

    const handleShowReplies = async () => {
        setShowReplies((prevShowReplies) => {
            const newValue = !prevShowReplies;
            if (newValue && repliesRef.current.length === 0 && replyCount > 0) {
                fetchReplies();
            }
            return newValue;
        });
    };

    useEffect(() => {
        if (!nextFocusId) {
            return undefined;
        }
        let cancelled = false;
        setShowReplies(true);
        const fallback = focusedCommentsById[nextFocusId];
        if (fallback) {
            setReplies((prev) => mergeFetchedComments(prev, [fallback]));
        }

        (async () => {
            setLoadingReplies(true);
            try {
                const fetched = await getUserCommentReplies(
                    userId,
                    comment.id,
                    0,
                    FOCUS_PAGE_SIZE
                );
                if (!cancelled) {
                    setReplies((prev) => mergeFetchedComments(prev, fetched?.data || []));
                }
            } catch (error) {
                console.error("Error loading focused replies:", error);
            } finally {
                if (!cancelled) {
                    setLoadingReplies(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [comment.id, focusedCommentsById, nextFocusId, userId]);

    useEffect(() => {
        if (!isFocusTarget) {
            return undefined;
        }
        setFlashing(true);
        const scrollId = setTimeout(() => {
            rootRef.current?.scrollIntoView({
                block: "center",
                behavior: "smooth",
                inline: "nearest",
            });
        }, 50);
        const timeoutId = setTimeout(() => setFlashing(false), FOCUS_FLASH_MS);
        return () => {
            clearTimeout(scrollId);
            clearTimeout(timeoutId);
        };
    }, [isFocusTarget]);

    const handleReply = (replyText) => {
        const optimistic = buildOptimisticComment({
            userId,
            user,
            postId: comment.post_id,
            parentId: comment.id,
            content: replyText,
        });
        applyIncomingReply(optimistic, { fromSelf: true });
        (async () => {
            try {
                const resp = await createComment({
                    post_id: comment.post_id,
                    parent_id: comment.id,
                    author_id: userId,
                    author_is_org: user?.type === "organization",
                    content: replyText,
                });
                if (resp?.success && resp.data) {
                    applyIncomingReply(resp.data, {
                        fromSelf: true,
                        replaceTempId: optimistic.id,
                    });
                } else {
                    console.error("Failed to create reply:", resp?.error);
                    let removed = false;
                    setReplies((prev) => {
                        const result = removeCommentById(prev, optimistic.id);
                        removed = result.removed;
                        return result.list;
                    });
                    if (removed) {
                        setReplyCount((count) => Math.max(0, count - 1));
                    }
                }
            } catch (error) {
                console.error("Error creating reply:", error);
                let removed = false;
                setReplies((prev) => {
                    const result = removeCommentById(prev, optimistic.id);
                    removed = result.removed;
                    return result.list;
                });
                if (removed) {
                    setReplyCount((count) => Math.max(0, count - 1));
                }
            }
        })();
    };

    const relativeTime = formatRelativeTimestamp(comment.created_at);
    const showRepliesToggle = showReplies
        ? replyCount >= MIN_REPLIES_TO_COLLAPSE
        : replyCount > 0 || Boolean(nextFocusId);
    const isReply = depth > 0;
    const isThreadAuthor =
        content?.author_id != null &&
        Number(content.author_id) === Number(comment.author_id) &&
        !!content.author_is_org === !!comment.author_is_org;

    if (removed) {
        return null;
    }

    const repliesLabel = showReplies
        ? "Hide replies"
        : replyCount > 0
            ? `View ${replyCount} ${replyCount === 1 ? "reply" : "replies"}`
            : "View replies";

    return (
        <li
            ref={rootRef}
            id={`comment-${comment.id}`}
            className={cn("min-w-0 scroll-mt-16", isReply && "thread-branch")}
        >
            {/* Row gap lives in the content column's padding so the permalink
                flash can wash a rounded area without shifting any text. */}
            <div
                className={cn(
                    "flex min-w-0 gap-1",
                    isReply ? "pt-2" : "pt-3",
                    !showReplies && !isReply && "pb-3"
                )}
            >
                {/* Left: Author profile picture. Stretches beside the body so
                    the stem can run from the PFP down to the replies list. */}
                <div
                    className={cn(
                        "thread-rail",
                        isReply && "thread-rail--reply",
                        showReplies && "thread-rail--open"
                    )}
                >
                    <UserLink
                        userId={comment.author_id}
                        isOrg={!!comment.author_is_org}
                        showProfilePic={true}
                        profilePicUrl={comment.author_pfp || defaultPfpUrl}
                        profilePicSize={isReply ? REPLY_AVATAR_CLASS : ROOT_AVATAR_CLASS}
                    />
                </div>
                {/* Right: Content */}
                <div
                    className={cn(
                        "flex min-w-0 flex-1 flex-col rounded-md px-2",
                        "transition-colors duration-700",
                        flashing && "bg-tag-accent"
                    )}
                >
                    {/* Row 1: Author name and timestamp */}
                    <div className="mb-0.5 flex min-w-0 items-center gap-2 text-compact text-text-secondary">
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                        <UserLink 
                            userId={comment.author_id}
                            isOrg={!!comment.author_is_org}
                            className="truncate font-emphasis text-text-primary hover:text-accent"
                        >
                            {comment.author_name || "Author"}
                        </UserLink>
                        {isThreadAuthor ? (
                            <Badge variant="accent" className="px-1.5 py-0">Author</Badge>
                        ) : null}
                        {relativeTime ? (
                            <span className="font-ui text-meta uppercase tracking-label text-text-faint">
                                {relativeTime}
                            </span>
                        ) : null}
                        </div>
                        {!isOptimistic ? (
                        <ContentOverflowMenu
                            userId={userId}
                            user={user}
                            item={comment}
                            contentType={comment.parent_id === 0 ? "comment" : "reply"}
                            onDeleted={() => {
                                setRemoved(true);
                                onDeleted?.(comment.id);
                            }}
                        />
                        ) : null}
                    </div>
                    {/* Row 2: Text content */}
                    <FormattedBody className="mb-1.5 text-compact text-text-primary" mentions={comment.mentions}>{comment.content}</FormattedBody>
                    {/* Row 3: Action bar */}
                    {!moderation ? (
                    <CommentActionBar
                        userId={userId}
                        user={user}
                        comment={comment}
                        canReply={!isOptimistic && depth < MAX_REPLY_DEPTH}
                        replyAvatarSize={REPLY_AVATAR_CLASS}
                        onReply={handleReply}
                        contentType={contentType}
                        content={content}
                    />
                    ) : null}
                    {/* Show replies button if there are replies */}
                    {showRepliesToggle && (
                        <button
                            className="flex items-center gap-1 self-start text-compact text-accent hover:underline"
                            onClick={handleShowReplies}
                            disabled={loadingReplies}
                            aria-expanded={showReplies}
                        >
                            <DisclosureChevronIcon expanded={showReplies} />
                            {repliesLabel}
                        </button>
                    )}
                </div>
            </div>
            {/* Replies: connectors are drawn from this comment's avatar. */}
            {showReplies && (
                <ul
                    className={cn(
                        "thread-replies min-w-0",
                        depth === 0 && "thread-replies--root",
                        depth + 1 >= INDENT_CAP_DEPTH && "thread-replies--tight"
                    )}
                >
                    {loadingReplies && replies.length === 0 ? (
                        <li className="thread-branch pt-1 text-compact text-text-muted" style={STUB_BRANCH_STYLE}>
                            Loading replies...
                        </li>
                    ) : (
                        <>
                            {replies.map(reply => (
                                <Comment
                                    key={reply.id}
                                    comment={reply}
                                    userId={userId}
                                    user={user}
                                    contentType={contentType}
                                    content={content}
                                    depth={depth + 1}
                                    focusCommentId={focusCommentId}
                                    focusChainIds={focusChainIds}
                                    focusedCommentsById={focusedCommentsById}
                                    onDeleted={(replyId) => {
                                        setReplies((prev) => prev.filter((r) => r.id !== replyId));
                                        setReplyCount((count) => Math.max(0, count - 1));
                                    }}
                                />
                            ))}
                            {replyCount > replies.length && (
                                <li className="thread-branch pt-1" style={STUB_BRANCH_STYLE}>
                                    <button
                                        className="font-mono text-meta font-emphasis uppercase tracking-label text-accent hover:underline"
                                        onClick={fetchReplies}
                                        disabled={loadingReplies}
                                    >
                                        Show more replies ({replyCount - replies.length})
                                    </button>
                                </li>
                            )}
                            {loadingReplies ? (
                                <li className="thread-branch pt-1 text-compact text-text-muted" style={STUB_BRANCH_STYLE}>
                                    Loading replies...
                                </li>
                            ) : null}
                        </>
                    )}
                </ul>
            )}
        </li>
    );
};

/**
 * Paginated comments for a post or event.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {object} props.post
 * @param {"post"|"event"} [props.contentType]
 * @param {object} [props.pendingComment] - Optimistic add/replace/remove payload, or a comment object.
 * @param {function} [props.onPendingCommentConsumed]
 * @param {number|null} [props.focusCommentId]
 */
function CommentList({
    userId,
    user,
    post,
    contentType = "post",
    pendingComment,
    onPendingCommentConsumed,
    focusCommentId = null,
}) {
    const commentsRef = useRef([]);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [commentCount, setCommentCount] = useState(post.comment_count || 0);
    const [focusChain, setFocusChain] = useState([]);

    commentsRef.current = comments;

    const focusChainIds = useMemo(
        () => focusChain.map((item) => Number(item.id)),
        [focusChain]
    );
    const focusedCommentsById = useMemo(() => {
        const map = {};
        for (const item of focusChain) {
            map[Number(item.id)] = item;
        }
        return map;
    }, [focusChain]);

    const applyIncomingComment = useCallback((comment, { replaceTempId } = {}) => {
        if (!comment || comment.id == null) {
            return;
        }
        let added = false;
        setComments((prev) => {
            const result = mergeIncomingComment(prev, comment, { replaceTempId });
            added = result.added;
            return result.list;
        });
        if (added) {
            setCommentCount((count) => count + 1);
        }
    }, []);

    const removePendingComment = useCallback((tempId) => {
        let removed = false;
        setComments((prev) => {
            const result = removeCommentById(prev, tempId);
            removed = result.removed;
            return result.list;
        });
        if (removed) {
            setCommentCount((count) => Math.max(0, count - 1));
        }
    }, []);

    const handleIncomingMessage = useCallback((data) => {
        if (data.type === 'new_comment') {
            applyIncomingComment(data.comment);
        }
    }, [applyIncomingComment]);

    useWebSocket(getWebSocketUrl(`/ws/comments/${post.id}/`), handleIncomingMessage);

    useEffect(() => {
        if (!pendingComment) {
            return;
        }
        const payload =
            pendingComment.comment ||
            pendingComment.replaceTempId ||
            pendingComment.removeTempId
                ? pendingComment
                : { comment: pendingComment };
        if (payload.removeTempId) {
            removePendingComment(payload.removeTempId);
        } else if (payload.comment) {
            applyIncomingComment(payload.comment, {
                replaceTempId: payload.replaceTempId,
            });
        }
        onPendingCommentConsumed?.();
    }, [
        pendingComment,
        applyIncomingComment,
        removePendingComment,
        onPendingCommentConsumed,
    ]);

    const fetchComments = async (limit = 4) => {
        if (commentCount === commentsRef.current.length && commentsRef.current.length > 0) {
            return;
        }
        if (commentsRef.current.length === 0) {
            setLoading(true);
        }
        try {
            const response = await getUserPostComments(
                userId,
                post.id,
                commentsRef.current.length,
                limit
            );
            if (response && response.success) {
                const page = response.data || [];
                setComments((prev) => mergeFetchedComments(prev, page));
            } else if (response && response.message) {
                console.error("Failed to fetch comments:", response.message);
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        setCommentCount(post.comment_count || 0);
    }, [post.comment_count]);

    useEffect(() => {
        let cancelled = false;
        commentsRef.current = [];
        setCommentCount(post.comment_count || 0);
        setComments([]);
        setFocusChain([]);
        (async () => {
            setLoading(true);
            try {
                const response = await getUserPostComments(userId, post.id, 0, 2);
                if (cancelled || !response?.success) {
                    return;
                }
                setComments((prev) => mergeFetchedComments(prev, response.data || []));
            } catch (error) {
                console.error("Error fetching comments:", error);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, post.id]);

    useEffect(() => {
        const targetId = Number(focusCommentId);
        if (!Number.isFinite(targetId) || targetId <= 0) {
            setFocusChain([]);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            const chain = await resolveCommentFocusChain(targetId);
            if (cancelled) {
                return;
            }
            const root = chain[0];
            if (!root || Number(root.post_id) !== Number(post.id)) {
                setFocusChain([]);
                return;
            }
            setFocusChain(chain);
            setComments((prev) => mergeFetchedComments(prev, [root]));
        })();
        return () => {
            cancelled = true;
        };
    }, [focusCommentId, post.id]);

    return (
        <div className="mt-2 min-w-0 overflow-x-hidden">
            {loading && comments.length === 0 && <div className="text-compact text-text-muted">Loading comments...</div>}
            {!loading && comments.length === 0 && commentCount === 0 && !focusChain.length ? (
                <div className="text-compact text-text-secondary">No comments yet</div>
            ) : null}
            <ul className="min-w-0 list-none space-y-1">
                {comments.map(comment => (
                    <Comment
                        key={comment.id}
                        comment={comment}
                        userId={userId}
                        user={user}
                        contentType={contentType}
                        content={post}
                        focusCommentId={focusCommentId}
                        focusChainIds={focusChainIds}
                        focusedCommentsById={focusedCommentsById}
                        onDeleted={(commentId) => {
                            setComments((prev) => prev.filter((c) => c.id !== commentId));
                            setCommentCount((count) => Math.max(0, count - 1));
                        }}
                    />
                ))}
            </ul>
            {commentCount > comments.length && commentCount > 0 && (
                <button
                    className="mt-2 text-compact text-accent hover:underline"
                    onClick={() => fetchComments()}
                    disabled={loading}
                >
                    Show more comments ({commentCount - comments.length})
                </button>
            )}
        </div>
    );
}

export default CommentList;
